const WhySafePage = () => {
  return (
    <div data-testid="why-safe-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              Why SAFE Exists
            </h1>
            <div className="space-y-4">
              <p className="text-lg md:text-xl text-slate-300">
                AI safety today is defined by guidance, policies, and internal testing.
              </p>
              <p className="text-lg md:text-xl text-slate-300">
                None of these prove a system is safe.
              </p>
              <p className="text-lg md:text-xl text-white font-semibold">
                Safety is being claimed. Not verified.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Current Approaches Fail */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Why Current Approaches Fail</p>
            <div className="space-y-4 mb-8">
              <p className="text-lg text-slate-700">Guidance does not enforce safety.</p>
              <p className="text-lg text-slate-700">Policies do not prevent harm.</p>
              <p className="text-lg text-slate-700">Internal testing is not independent verification.</p>
            </div>
            <div className="border-l-4 border-slate-900 pl-6 mt-8">
              <p className="text-lg text-slate-900 font-medium mb-2">
                The entity building the system is also defining and assessing its safety.
              </p>
              <p className="text-lg text-slate-900 font-semibold">
                This is a structural failure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Gap */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">The Gap</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-8">
              Every high-risk system requires independent verification.
            </h2>
            <div className="space-y-3 mb-8">
              <p className="text-lg text-slate-300">Aviation.</p>
              <p className="text-lg text-slate-300">Pharmaceuticals.</p>
              <p className="text-lg text-slate-300">Infrastructure.</p>
            </div>
            <p className="text-lg text-slate-300 mb-4">In each case, safety is not self-declared.</p>
            <p className="text-xl text-white font-semibold">AI is the exception.</p>
          </div>
        </div>
      </section>

      {/* What's Missing */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">What's Missing</p>
            <div className="space-y-4">
              <p className="text-lg text-slate-700">There is no independent standard defining what safety is.</p>
              <p className="text-lg text-slate-700">There is no independent body verifying whether systems meet it.</p>
              <p className="text-lg text-slate-700">There is no separation between builder and assessor.</p>
            </div>
            <p className="text-lg text-slate-900 font-semibold mt-8">
              Without this, safety cannot be proven.
            </p>
          </div>
        </div>
      </section>

      {/* Consequence */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Consequence</p>
            <h2 className="heading-2 mb-8">
              Without independent verification:
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Safety claims cannot be trusted.</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Harm is addressed after it occurs.</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Accountability does not exist.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What SAFE Changes */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">What SAFE Changes</p>
            <p className="text-lg text-slate-900 font-semibold mb-6">SAFE introduces structural separation.</p>
            <div className="space-y-4">
              <p className="text-lg text-slate-700">SAFE defines what safety is.</p>
              <p className="text-lg text-slate-700">SAFE Labs verifies systems independently.</p>
              <p className="text-lg text-slate-700">SAIL implements safety within systems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Shift */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">The Shift</p>
            <p className="text-lg text-slate-300 mb-8">Safety moves from:</p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-lg text-slate-400 line-through">claimed</span>
                <span className="text-slate-500">&rarr;</span>
                <span className="text-lg text-white font-semibold">proven</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-lg text-slate-400 line-through">internal</span>
                <span className="text-slate-500">&rarr;</span>
                <span className="text-lg text-white font-semibold">independent</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-lg text-slate-400 line-through">reactive</span>
                <span className="text-slate-500">&rarr;</span>
                <span className="text-lg text-white font-semibold">enforced</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Line */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
            If a system cannot be independently verified,
          </p>
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mt-2">
            it cannot be considered safe.
          </p>
        </div>
      </section>
    </div>
  );
};

export default WhySafePage;
